import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import AOS from "aos";
import API from "../api";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import SEO from "../components/SEO";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const CourseDetailsPage = () => {
    const { id } = useParams();

    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, [id]);

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // const apiUrl = 'http://localhost:4000';

    useEffect(() => {
        let mounted = true;
        const fetchCourse = async () => {
            setLoading(true);
            setError(null);
            try {
                // Try endpoint for single course first
                const res = await API.get(`/courses/${id}`);
                const data = res.data && (res.data.course || res.data.data || res.data) ? (res.data.course || res.data.data || res.data) : null;
                if (mounted && data) {
                    setCourse(data);
                    return;
                }

                // Fallback: fetch all courses and find by id
                const allRes = await API.get('/courses');
                const allData = allRes.data && (Array.isArray(allRes.data) ? allRes.data : allRes.data.courses || allRes.data.data) ? (Array.isArray(allRes.data) ? allRes.data : allRes.data.courses || allRes.data.data) : [];
                const courseId = Number(id);
                const found = (Array.isArray(allData) ? allData : []).find((c) => Number(c.id) === courseId);
                if (mounted) setCourse(found || null);
            } catch (err) {
                console.error('Failed to load course', err);
                if (mounted) setError(err.message || 'Failed to load course');
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchCourse();

        return () => {
            mounted = false;
        };
    }, [id]);

    let courseImage = course?.image;

    const heroSlides = [
        {
            id: course ? course.id : "not-found",
            image: `${apiUrl + courseImage}` || "/assets/img/course-details.jpg",
            title: course?.title || "Course Details",
            subtitle: course?.description || "Learn more about this course.",
            buttonText: course ? "Enroll Now" : "Back to Courses",
            buttonLink: course ? "/#contact" : "/courses",
        },
    ];

    const courseUrl = typeof window !== 'undefined' ? window.location.href : `https://africaresearchuniversity.com/courses/${id}`;

    const courseJsonLd = course ? {
        '@context': 'https://schema.org',
        '@type': 'Course',
        'name': course.title,
        'description': course.introduction || course.description || '',
        'provider': { '@type': 'Organization', 'name': 'Africa Research University', 'url': 'https://africaresearchuniversity.com' }
    } : null;

    return (
        <>
            <SEO 
                title={course ? `${course.title} - ARU` : 'Course - ARU'} 
                description={course?.introduction || course?.description} 
                url={courseUrl} 
                jsonLd={courseJsonLd} 
                image={apiUrl + courseImage}
                breadcrumbs={[
                    { name: 'Home', url: 'https://africaresearchuniversity.com' },
                    { name: 'Courses', url: 'https://africaresearchuniversity.com/courses' },
                    { name: course?.title || 'Course', url: courseUrl }
                ]}
            />
            <section className="py-1">
                {/* Hero (replaces Page Title) */}
                <Hero slides={heroSlides} interval={5000} height="320px" borderRadius="50px" hidden={true} />

                <div className="container mx-auto px-4 mt-8 grid md:grid-cols-3 gap-8" data-aos="fade-up">
                    <main className="md:col-span-2">
                        {loading ? (
                            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-600">Loading course...</div>
                        ) : error ? (
                            <div className="bg-white rounded-lg shadow p-6 text-center text-red-600">{error}</div>
                        ) : course ? (
                            <article className="bg-white rounded-lg shadow p-6">
                                <h1 className="text-2xl font-bold mb-2">{course.title}</h1>
                                <p className="text-sm text-green-700 font-medium mb-4">{course.category} • {course.method_of_study}</p>

                                <section className="mb-6 prose prose-sm sm:prose lg:prose-lg">
                                    <h2 className="text-lg font-semibold mb-2">Introduction</h2>
                                    {course.introduction ? (
                                        <div
                                            className="text-gray-700"
                                            dangerouslySetInnerHTML={{ __html: course.introduction }}
                                        />
                                    ) : (
                                        <p>No introduction available.</p>
                                    )}
                                </section>

                                {/* <section>
                                <h2 className="text-lg font-semibold mb-2">Who should attend</h2>
                                <p className="text-gray-700">{course.who_should_attend}</p>
                            </section> */}

                                <section className="mb-6 prose prose-sm sm:prose lg:prose-lg">
                                    <h2 className="text-lg font-semibold mb-2">Who Should Attend</h2>
                                    {course.who_should_attend ? (
                                        <div
                                            className="text-gray-700"
                                            dangerouslySetInnerHTML={{ __html: course.who_should_attend }}
                                        />
                                    ) : (
                                        <p>No information available.</p>
                                    )}
                                </section>
                            </article>
                        ) : (
                            <div className="bg-white rounded-lg shadow p-6">
                                <p className="text-gray-700">We couldn't find the course you're looking for.</p>
                                <Link to="/courses" className="inline-block mt-4 text-green-600 hover:underline">Back to courses</Link>
                            </div>
                        )}
                    </main>

                    <aside>
                        {course && (
                            <div className="bg-white rounded-lg shadow p-6 space-y-4">
                                <img src={apiUrl + courseImage} alt={course.title} className="rounded-md w-full object-cover" />
                                <div>
                                    <h4 className="font-semibold">Method of delivery</h4>
                                    <p className="text-gray-700">{course.method_of_study}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Category</h4>
                                    <p className="text-gray-700">{course.category}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Duration</h4>
                                    <p className="text-gray-700">{course.duration}</p>
                                </div>
                                {course.document && (
                                    <a
                                        href={`${apiUrl}${course.document}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-center bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                    >
                                        {'Download Course Document(s)'}
                                    </a>
                                )}
                                <Link to="/courses" className="block text-center bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">Browse courses</Link>
                            </div>
                        )}
                    </aside>
                </div>
            </section>
        </>
    );
};

export default CourseDetailsPage;
